import { useEffect, useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useUIStore } from '../store/uiStore.js'
import { weekLabel, currentWeekKey, timeAgo } from '../data/metaKeys.js'
import { TEAMS } from '../data/teams.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'
import RichText from './RichText.jsx'
import SummaryBulletList from './SummaryBulletList.jsx'

/**
 * WeeklyArchivePanel — slide-in read-only view of prior weekly snapshots.
 *
 * Driven by uiStore.archiveWeekKey:
 *   null     → panel closed
 *   'latest' → panel open, show the most recent snapshot
 *   <key>    → panel open, show the snapshot for that specific week
 *
 * Shows each snapshot's:
 *   - Executive summary (TLDR + flags)
 *   - Weekly TLDR/Wins/Flags per goal
 *   - Action items (task, owner, status, due date)
 *
 * Archive entries are frozen at snapshot time, so the state shown here
 * never changes — it's the historical record.
 */
export default function WeeklyArchivePanel() {
  const selected = useUIStore((s) => s.archiveWeekKey)
  const close = useUIStore((s) => s.closeArchive)
  const setWeek = useUIStore((s) => s.setArchiveWeek)
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()

  const history = meta?.weeklyHistory || {}

  // Sorted list of week keys, newest first
  const weeks = useMemo(
    () => Object.keys(history).sort().reverse(),
    [history]
  )

  // Resolve `selected === 'latest'` to the newest week available
  const weekKey =
    selected === 'latest' ? weeks[0] || null : selected

  // Escape to close
  useEffect(() => {
    if (!selected) return
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected, close])

  if (!selected) return null

  const snapshot = weekKey ? history[weekKey] : null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 895,
        }}
      />

      {/* Panel */}
      <aside
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          width: 480,
          maxWidth: '95vw',
          height: '100vh',
          background: '#fff',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 910,
          fontFamily: 'inherit',
        }}
      >
        <header
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>
              📅 Weekly Archive
            </span>
            <button
              onClick={close}
              title="Close"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                fontSize: 18,
                color: 'var(--muted-light)',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Week selector */}
          {weeks.length > 0 ? (
            <select
              value={weekKey || ''}
              onChange={(e) => setWeek(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
                fontFamily: 'inherit',
                outline: 'none',
                background: '#fff',
              }}
            >
              {weeks.map((k) => (
                <option key={k} value={k}>
                  {weekLabel(k)}
                  {k === currentWeekKey() ? ' · current' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              No snapshots yet. The first archive runs automatically at the start of next week.
            </div>
          )}

          {/* Open the full dashboard view for this past week. Only shown
              when the snapshot includes goals data (snapshots before the
              full-state capture only have exec summary / weekly summaries
              and can't render the full dashboard). */}
          {snapshot?.goals && (
            <button
              type="button"
              onClick={() => {
                useArchiveViewStore.getState().enterArchive(weekKey)
                close()
              }}
              style={{
                padding: '6px 10px',
                border: '1px solid #fde68a',
                background: '#fef3c7',
                color: '#78350f',
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 6,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              🕰️ View the full dashboard for this week →
            </button>
          )}
        </header>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
          }}
        >
          {snapshot ? (
            <ArchiveView snapshot={snapshot} goalsData={goalsData} />
          ) : (
            <EmptyArchive />
          )}
        </div>
      </aside>
    </>
  )
}

/* ─── Snapshot view ─────────────────────────────────────────────────── */

function ArchiveView({ snapshot, goalsData }) {
  const { weekStart, createdAt, execSummary, weeklySummary, actionItems, teamUpdates } = snapshot

  // Flatten weeklySummary into a list keyed by goal name
  const goalEntries = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (const [key, value] of Object.entries(weeklySummary || {})) {
      const match = /^p(\d+)-g(\d+)$/.exec(key)
      if (!match) continue
      const pi = Number(match[1])
      const gi = Number(match[2])
      const pillar = goalsData[pi]
      const goal = pillar?.goals?.[gi]
      if (!goal) continue
      if (!value?.tldr && !value?.wins && !value?.flags) continue
      out.push({
        key,
        pillar: pillar.pillar,
        goalName: goal.name || 'Untitled goal',
        tldr: value.tldr || '',
        wins: value.wins || '',
        flags: value.flags || '',
        savedBy: value.savedBy,
        savedAt: value.savedAt,
      })
    }
    return out
  }, [weeklySummary, goalsData])

  // Team-level updates preserved in the snapshot — list each team's
  // TLDR / Wins / Flags in the order defined by the TEAMS config so the
  // archive layout is stable across weeks.
  const teamEntries = useMemo(() => {
    const map = teamUpdates || {}
    const out = []
    for (const team of TEAMS) {
      const entry = map[team.id]
      if (!entry) continue
      if (!entry.tldr && !entry.wins && !entry.flags) continue
      out.push({
        teamId: team.id,
        teamName: team.name,
        teamIcon: team.icon,
        teamColor: team.color,
        dri: entry.dri || team.defaultDri || null,
        tldr: entry.tldr || '',
        wins: entry.wins || '',
        flags: entry.flags || '',
        savedBy: entry.savedBy,
        savedAt: entry.savedAt,
      })
    }
    return out
  }, [teamUpdates])

  // Flatten actionItems into a per-goal grouped list
  const actionItemGroups = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (const [key, items] of Object.entries(actionItems || {})) {
      const match = /^p(\d+)-g(\d+)$/.exec(key)
      if (!match || !Array.isArray(items) || items.length === 0) continue
      const pi = Number(match[1])
      const gi = Number(match[2])
      const pillar = goalsData[pi]
      const goal = pillar?.goals?.[gi]
      if (!goal) continue
      out.push({
        key,
        pillar: pillar.pillar,
        goalName: goal.name || 'Untitled goal',
        items,
      })
    }
    return out
  }, [actionItems, goalsData])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted)',
          padding: '8px 12px',
          background: '#fafafa',
          borderRadius: 6,
          border: '1px solid var(--border)',
        }}
      >
        Snapshot of week starting <strong>{weekLabel(weekStart)}</strong>
        {createdAt && (
          <>
            <br />
            Archived {timeAgo(new Date(createdAt).getTime())}
          </>
        )}
      </div>

      {/* Executive summary — render bullets when the snapshot has them,
          otherwise fall back to the legacy prose blocks so old archives
          (pre-bullet redesign) keep displaying correctly. */}
      {execSummary &&
        (execSummary.tldr ||
          execSummary.winsSummary ||
          execSummary.flagsSummary ||
          (execSummary.winBullets && execSummary.winBullets.length > 0) ||
          (execSummary.flagBullets && execSummary.flagBullets.length > 0)) && (
        <Section title="✨ Executive Summary">
          {execSummary.tldr && (
            <Block label="TLDR" text={execSummary.tldr} />
          )}

          {execSummary.winBullets && execSummary.winBullets.length > 0 ? (
            <ArchiveBulletBlock
              label="Wins"
              labelColor="#166534"
              bullets={execSummary.winBullets}
              accent="#0F6E56"
            />
          ) : execSummary.winsSummary ? (
            <Block
              label="Wins"
              text={execSummary.winsSummary}
              labelColor="#166534"
            />
          ) : null}

          {execSummary.flagBullets && execSummary.flagBullets.length > 0 ? (
            <ArchiveBulletBlock
              label="Flags & Risks"
              labelColor="#b91c1c"
              bullets={execSummary.flagBullets}
              accent="#A32D2D"
            />
          ) : execSummary.flagsSummary ? (
            <Block
              label="Flags & Risks"
              text={execSummary.flagsSummary}
              labelColor="#b91c1c"
            />
          ) : null}

          {execSummary.generatedAt && (
            <div style={{ fontSize: 10, color: 'var(--muted-light)', marginTop: 4 }}>
              {execSummary.edited ? 'Edited' : 'Generated'}{' '}
              {execSummary.generatedBy ? `by ${execSummary.generatedBy} ` : ''}
              on {new Date(execSummary.generatedAt).toLocaleDateString()}
            </div>
          )}
        </Section>
      )}

      {/* Team-level updates */}
      {teamEntries.length > 0 && (
        <Section title={`📣 Team Updates (${teamEntries.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {teamEntries.map((e) => (
              <div
                key={e.teamId}
                style={{
                  padding: '10px 12px',
                  background: '#fafafa',
                  border: '1px solid var(--border)',
                  borderLeft: `4px solid ${e.teamColor}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{e.teamIcon}</span>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: e.teamColor,
                    }}
                  >
                    {e.teamName}
                  </div>
                  {e.dri && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--muted)',
                      }}
                    >
                      DRI: {e.dri}
                    </span>
                  )}
                </div>
                {e.tldr && <Block label="TLDR" text={e.tldr} />}
                {e.wins && <Block label="Wins" text={e.wins} labelColor="#166534" />}
                {e.flags && <Block label="Flags" text={e.flags} labelColor="#b91c1c" />}
                {(e.savedAt || e.savedBy) && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--muted-light)',
                      marginTop: 4,
                    }}
                  >
                    {e.savedBy ? `${e.savedBy} · ` : ''}
                    {e.savedAt ? new Date(e.savedAt).toLocaleDateString() : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Weekly updates per goal */}
      {goalEntries.length > 0 && (
        <Section title={`📝 Weekly Updates (${goalEntries.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {goalEntries.map((e) => (
              <div
                key={e.key}
                style={{
                  padding: '10px 12px',
                  background: '#fafafa',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--muted)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.06,
                    marginBottom: 2,
                  }}
                >
                  {e.pillar}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--text)',
                    marginBottom: 8,
                  }}
                >
                  {e.goalName}
                </div>
                {e.tldr && <Block label="TLDR" text={e.tldr} />}
                {e.wins && <Block label="Wins" text={e.wins} labelColor="#166534" />}
                {e.flags && <Block label="Flags" text={e.flags} labelColor="#b91c1c" />}
                {(e.savedAt || e.savedBy) && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--muted-light)',
                      marginTop: 4,
                    }}
                  >
                    {e.savedBy ? `${e.savedBy} · ` : ''}
                    {e.savedAt ? new Date(e.savedAt).toLocaleDateString() : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Action items */}
      {actionItemGroups.length > 0 && (
        <Section
          title={`🎯 Action Items (${actionItemGroups.reduce(
            (sum, g) => sum + g.items.length,
            0
          )})`}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {actionItemGroups.map((g) => (
              <div key={g.key}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--text-secondary)',
                    marginBottom: 4,
                  }}
                >
                  {g.pillar} › {g.goalName}
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  {g.items.map((item) => {
                    const done = item.status === 'done'
                    return (
                      <li
                        key={item.id}
                        style={{
                          padding: '4px 8px',
                          fontSize: 12,
                          display: 'flex',
                          gap: 6,
                          alignItems: 'baseline',
                          color: done ? 'var(--muted)' : 'var(--text)',
                          textDecoration: done ? 'line-through' : 'none',
                        }}
                      >
                        <span>{done ? '✓' : '○'}</span>
                        <span style={{ flex: 1 }}>{item.task}</span>
                        {item.owner && item.owner !== '—' && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: 'var(--muted)',
                            }}
                          >
                            {item.owner}
                          </span>
                        )}
                        {item.dueDate && (
                          <span
                            style={{
                              fontSize: 10,
                              color: 'var(--muted-light)',
                            }}
                          >
                            {item.dueDate}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {goalEntries.length === 0 &&
        actionItemGroups.length === 0 &&
        teamEntries.length === 0 &&
        !execSummary && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--muted-light)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '24px 0',
            }}
          >
            This snapshot is empty — no summaries, updates, or action items were
            captured for this week.
          </div>
        )}
    </div>
  )
}

function EmptyArchive() {
  return (
    <div
      style={{
        fontSize: 12,
        color: 'var(--muted)',
        textAlign: 'center',
        padding: '40px 16px',
        lineHeight: 1.6,
      }}
    >
      No weekly snapshots yet.
      <br />
      <br />
      The archive runs automatically at the start of each new week — the first
      client to visit after Monday creates a snapshot of the prior week's
      summaries, exec summary, and action items.
    </div>
  )
}

/* ─── Small building blocks ──────────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: 'var(--text)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 8,
          paddingBottom: 4,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

function Block({ label, text, labelColor }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: labelColor || 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        <RichText text={text} />
      </div>
    </div>
  )
}

/**
 * ArchiveBulletBlock — label + bulleted list for archived exec
 * summaries that used the new structured-bullet format. Parallels the
 * legacy Block prose helper, but delegates rendering to the shared
 * SummaryBulletList so the visual presentation stays identical to
 * the live Executive Summary card.
 */
function ArchiveBulletBlock({ label, labelColor, bullets, accent }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: labelColor || 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <SummaryBulletList bullets={bullets} accent={accent} />
    </div>
  )
}
